import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { Translation, getI18n } from 'react-i18next';
import { Table, Button, Space } from 'antd';
import ListActions from '../../../elements/components/ListActions';
import { StatusTag, BooleanTag } from '../../../elements/components/Tags';
import {
  getColumnSearchProps,
  getColumnDateRangeProps,
} from '../../../elements/components/TableColumnFilters';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';
import { EyeOutlined } from '@ant-design/icons';

const CareGuideDocumentsList = ({
  component,
  page,
  limit,
  order,
  filter,
  total,
  load,
  remove,
  history,
  ...props
}) => {
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    delete filter[dataIndex];
    history.push(
      QueryString.append(props.location.pathname, { order, ...filter })
    );
  };

  const columns = [
    {
      title: getI18n().t('care_guide_documents_care_guide_name'),
      dataIndex: 'care_guide_name',
      key: 'care_guide_name',
      ...getColumnSearchProps(
        'care_guide_name',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_care_guide_name'),
        filter
      ),
    },
    {
      title: getI18n().t('care_guide_documents_partnership_name'),
      dataIndex: 'partnership_name',
      key: 'partnership',
      ...getColumnSearchProps(
        'partnership',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_partnership_name'),
        filter
      ),
    },
    {
      title: getI18n().t('care_guide_documents_partnership_plan'),
      dataIndex: 'partnership_plan',
      key: 'plan',
      ...getColumnSearchProps(
        'plan',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_partnership_plan'),
        filter
      ),
    },
    {
      title: getI18n().t('care_guide_documents_profile_username'),
      dataIndex: 'profile_username',
      key: 'profile_username',
      ...getColumnSearchProps(
        'username',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_profile_username'),
        filter
      ),
    },
    {
      title: getI18n().t('care_guide_documents_is_pinned'),
      dataIndex: 'is_pinned',
      key: 'is_pinned',
      filters: [
        { text: getI18n().t('boolean_true'), value: 1 },
        { text: getI18n().t('boolean_false'), value: 0 },
      ],
      filteredValue:
        'is_pinned' in filter ? filter['is_pinned'].split(',') : null,
      render: (val) => <BooleanTag value={val} />,
    },
    {
      title: getI18n().t('care_guide_documents_file_name'),
      dataIndex: 'file_name',
      key: 'file_name',
      ...getColumnSearchProps(
        'file_name',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_file_name'),
        filter
      ),
    },
    {
      title: getI18n().t('care_guide_documents_user_file_name'),
      dataIndex: 'user_file_name',
      key: 'user_file_name',
      ...getColumnSearchProps(
        'user_file_name',
        handleSearch,
        handleReset,
        getI18n().t('care_guide_documents_user_file_name'),
        filter
      ),
    },
    {
      title: getI18n().t('table_header_status'),
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: getI18n().t('status_enabled'), value: 1 },
        { text: getI18n().t('status_disabled'), value: 2 },
        { text: getI18n().t('status_archived'), value: 3 },
        { text: getI18n().t('status_deleted'), value: 4 },
        { text: getI18n().t('status_pending'), value: 5 },
      ],
      filteredValue: 'status' in filter ? filter['status'].split(',') : null,
      render: (code) => <StatusTag status={code} />,
    },
    {
      title: getI18n().t('care_guide_documents_uploaded_at'),
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'uploaded_at.asc'
          ? 'ascend'
          : order === 'uploaded_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'uploaded_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_updated_at'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'updated_at.asc'
          ? 'ascend'
          : order === 'updated_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'updated_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_actions'),
      key: 'actions',
      render: (item) => (
        <>
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={(e) =>
                history.push(
                  pathTo('CareGuideDocumentViewScreen', { id: item.id })
                )
              }
            />
            <ListActions
              id={item.id}
              page={page}
              limit={limit}
              order={order}
              filter={filter}
              load={load.bind(this)}
              remove={remove.bind(this)}
              editScreen="CareGuideDocumentEditScreen"
            />
          </Space>
        </>
      ),
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, { page: pagination['current'] });
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order =
          sorter['field'] +
          '.' +
          (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  };

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(page, limit, order, JSON.parse(filterString));
  }, [page, limit, order, filterString, load]);

  return (
    <Translation>
      {(t) => (
        <Table
          dataSource={props.list}
          columns={columns}
          pagination={page === 1 && total <= limit ? false : pagination}
          loading={props.isLoading}
          scroll={{ x: true }}
          onChange={handleTableChange}
        />
      )}
    </Translation>
  );
};

export default withRouter(CareGuideDocumentsList);

Logger.log('silly', `CareGuideDocumentsList loaded.`);
